import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { Typography } from '@/components/ui/typography';
import { Button } from '@/components/ui/button';
import AuthHeroLayout from './components/AuthHeroLayout';
import AdditionalDetailsForm from './components/AdditionalDetailsForm';
import { usePostRegistrationRedirect } from './hooks/usePostRegistrationRedirect';
import type { AdditionalDetailsFormValues } from '../../types/registration.types';

interface EntityData {
  firstName: string;
  lastName: string;
  companyName: string;
  website: string;
  phone: string;
  properties: string;
}

const BLANK_VALUES: AdditionalDetailsFormValues = {
  firstName: '',
  lastName: '',
  companyName: '',
  website: '',
  phone: '',
  properties: ''
};

// Single-name Google SSO users come back from the backend with lastName
// stored as "." (a placeholder satisfying a NOT-NULL constraint in
// user-service). Normalize to blank so the form field doesn't prefill
// with a period.
const SSO_LASTNAME_PLACEHOLDER = '.';

const readEntityData = (): Partial<EntityData> => {
  try {
    return JSON.parse(localStorage.getItem('entityData') ?? '{}');
  } catch {
    return {};
  }
};

const AdditionalDetailsPage = () => {
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');

  const [initialValues] = useState<AdditionalDetailsFormValues>(() => {
    if (userId === null) return BLANK_VALUES;
    const data = readEntityData();
    return {
      firstName: data.firstName ?? '',
      lastName:
        data.lastName === SSO_LASTNAME_PLACEHOLDER ? '' : data.lastName ?? '',
      companyName: data.companyName ?? '',
      phone: data.phone ?? '',
      properties: data.properties ?? '',
      website: data.website ?? ''
    };
  });

  const { submit, resume, endSession, isProcessing, isSearching, isUnconfirmed } =
    usePostRegistrationRedirect();
  // The form is offered only once `resume` has found no account this page
  // should send the user to, or may be about to (DV-3745 follow-up).
  const [formOffered, setFormOffered] = useState(false);

  useEffect(() => {
    let live = true;
    resume(userId)
      .then((withheld) => {
        if (live && !withheld) setFormOffered(true);
      })
      .catch((error: unknown) => {
        // Not knowing reads as "no account", as when the check cannot answer.
        // A rejection after an account was found leaves `isProcessing` set, so
        // the form stays hidden even then.
        console.warn('additional-details: the on-load account check failed', error);
        if (live) setFormOffered(true);
      });
    return () => {
      live = false;
    };
    // Once, as the page loads: `resume` is a fresh closure every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formHidden = isProcessing || !formOffered || isUnconfirmed;
  const busy = formHidden && !isUnconfirmed;
  // The on-load check and the patient search for an account that may be
  // landing offer a way out. A submit's own request, and the refresh and
  // lookup after its 2xx, do not: the account is being created or opened.
  const signOutOffered = isSearching || !isProcessing;
  const signOutLink = (
    <Button
      type="button"
      variant="link"
      // Standalone link-styled action: the 44px touch floor, as on the form's.
      className="max-lg:min-h-11"
      onClick={endSession}
    >
      Sign Out
    </Button>
  );

  useEffect(() => {
    document.title = 'PlanOmatic - Register';
    return () => {
      document.title = 'PlanOmatic';
    };
  }, []);

  const handleSubmit = (
    values: AdditionalDetailsFormValues,
    setFieldError: (field: string, message: string) => void
  ) => {
    submit({
      values,
      userId,
      setFieldError
    });
  };

  return (
    <AuthHeroLayout
      cardMaxWidth="max-w-[480px]"
      ariaLabelledBy="additional-details-heading"
    >
      <h1 id="additional-details-heading" className="sr-only">
        Complete your registration
      </h1>

      {isUnconfirmed && (
        <div className="flex flex-col items-center gap-2 py-6">
          <Typography as="p" variant="body-medium" className="text-center">
            We couldn't confirm your registration yet
          </Typography>
          <Typography as="p" variant="body" className="text-center">
            Your earlier submission may still be completing. Reload this page in a minute to
            check again, or contact support@planomatic.com.
          </Typography>
          {signOutLink}
        </div>
      )}
      {busy && (
        <div className="flex flex-col items-center gap-2 py-6">
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="animate-spin text-primary h-6 w-6" />
            <Typography as="p" variant="body-medium">
              {isSearching ? 'Finishing your registration...' : 'Processing... Please wait'}
            </Typography>
          </div>
          {isSearching && (
            <Typography as="p" variant="body" className="text-center">
              Checking on the registration you already submitted. This can take a minute
              or two.
            </Typography>
          )}
          {signOutOffered && signOutLink}
        </div>
      )}
      {/* Hidden, never unmounted, while withheld (DV-3745): a refused
          registration brings the form back with what was typed and the
          field error set on it — an unmounted form loses both, and a
          duplicate-name error the user never sees ends in a renamed
          resubmit. */}
      <div hidden={formHidden}>
        <AdditionalDetailsForm
          initialValues={initialValues}
          isLoading={isProcessing}
          onSubmit={handleSubmit}
          onSignOut={endSession}
        />
      </div>
    </AuthHeroLayout>
  );
};

export default AdditionalDetailsPage;
